import React, { useContext, useEffect, useCallback, useState } from "react";
import { Text, TouchableWithoutFeedback }                      from "react-native";
import { StyleSheet, View, FlatList }                          from "react-native";
import { useHistory }                                          from "react-router-dom";

import DayJs                                                   from "~/helpers/day";

import Row                                                     from "~/layouts/Row";
import Col                                                     from "~/layouts/Col";
import Avatar                                                  from "~/components/Avatar";
import SolidButton                                             from "../Buttons/SolidButton/index";
import LineFilterBar                                           from "../Bars/LineFilterBar";
import palette                                                 from "~/styles/palette";

import AppContext                                              from "../../contexts/AppContext";
import UserContext                                             from "../../contexts/UserContext";
import ThreadsContext                                          from "../../contexts/ThreadsContext";
import ConversationContext                                     from "../../contexts/ConversationContext";
import Engage                                                  from '../../services/engage';
import useTwilio               from "~/hooks/useTwilio";

export default function ConversationList() {
  const history = useHistory();
  const { threads, filter, setFilter, nextPage } = useContext(ThreadsContext);
  const { conversation, clearConversation, setConversationById, } = useContext(ConversationContext);
  const { user } = useContext(UserContext);

  const { startCall } = useTwilio();

  const onNewCall = () => {
    startCall(conversation?.participant?.phone_number, conversation?.participant);
  };

  const onCreateNewThread = useCallback(() => {
    clearConversation();
    history.push("/messages/new");
  }, [history, clearConversation]);

  const { compactMode } = useContext(AppContext);

  const onPress = useCallback(
    (conversation) => () => {
      setConversationById(conversation)
      Engage.markConversationAsRead(conversation.id)
      history.push(`${compactMode ? "/compact" : ""}/messages/${conversation.id}`);
    },
    [history, setConversationById, compactMode]
  );

  return (
    <View style={[styles.conversation_list__container]}>
      <ConversationActions onNewThread={onCreateNewThread} onNewCall={onNewCall} hasConversation={!!conversation?.id} />
      <LineFilterBar
        activeFilter={filter}
        onFilterChange={(activeFilter) => setFilter(activeFilter)}
      />
      <FlatList
        data={threads}
        ListEmptyComponent={NoConversationsComponent}
        renderItem={renderConversationListItem({ user, onPress })}
        keyExtractor={(item) => `${item.id}`}
        onEndReachedThreshold={0.9}
        onEndReached={nextPage}
      />
    </View>
  );
}

const ConversationActions = ({ onNewThread, onNewCall, hasConversation }) => {
  return (
    <Row
      centered
      mb={8}
      style={{
        height: 75,
        borderBottomWidth: 1,
        borderColor: palette.light_grey,
      }}
    >
      <Col maxWidth={175} rightCenter style={{ height: "100%" }} pr={8}>
        <SolidButton
          onPress={onNewCall}
          label="Start Call"
          disabled={!hasConversation}
          icon="phone"
          color={palette.primary}
        />
      </Col>
      <Col leftCenter style={{ height: "100%" }} pl={8}>
        <SolidButton
          label="New Text"
          onPress={onNewThread}
          icon="message-circle"
          color={palette.success}
        />
      </Col>
    </Row>
  );
};

const NoConversationsComponent = () => {
  return (
    <View style={[styles.no_conversations_component]}>
      <Text style={[styles.no_conversations_component_text]}>
        No conversations have been started
      </Text>
    </View>
  );
};

const renderConversationListItem =
  ({ user, onPress }) =>
  ({ item }) => {
    const unreadCls =
      item.unread_count > 0
        ? styles.conversation_list__list_item_unread_cls
        : false;

    return (
      <TouchableWithoutFeedback onPress={onPress(item)}>
        <View style={[styles.conversation_list__list_item]}>
          <Row>
            <Col
              leftCenter
              style={[styles.conversation_list__list_item_avatar_container]}
            >
              <Avatar
                placeholder
                size={45}
                first_name={item.participant.first_name}
                last_name={item.participant.last_name}
                source={item.participant.avatar}
              />
            </Col>
            <Col>
              <Text
                numberOfLines={1}
                ellipsizeMode="tail"
                style={[styles.conversation_list__list_item_name, unreadCls]}
              >
                {item.participant.name}
                {item.unread_count > 0 && (
                  <Text
                    style={[
                      styles.conversation_list__list_item_read_indicator,
                      unreadCls,
                    ]}
                  >
                    &#8226;
                  </Text>
                )}
              </Text>
              <Text
                style={[
                  styles.conversation_list__list_item_preview_text,
                  unreadCls,
                ]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {item.preview}
              </Text>
              <Text
                numberOfLines={1}
                ellipsizeMode="tail"
                style={[styles.conversation_list__list_item__timestamp]}
              >
                {DayJs(item.timestamp).fromNow()}
              </Text>
            </Col>
          </Row>
        </View>
      </TouchableWithoutFeedback>
    );
  };

const styles = StyleSheet.create({
  conversation_list__container: {
    flex: 1,
  },
  conversation_list__list_item: {
    height: 90,
    paddingHorizontal: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: palette.light_grey,
    justifyContent: "center",
    cursor: "pointer",
  },
  conversation_list__list_item_avatar_container: {
    maxWidth: 55,
  },
  conversation_list__list_item_read_indicator: {
    color: palette.primary_light,
    fontSize: 24,
    marginLeft: 10,
  },
  conversation_list__list_item_preview_text: {
    fontSize: 16,
    // fontFamily: "acumin-pro, san-serif",
  },
  conversation_list__list_item_name: {
    fontSize: 15,
    fontWeight: "500",
    // fontFamily: "acumin-pro, san-serif",
  },
  conversation_list__list_item_unread_cls: {
    fontWeight: "600",
  },
  conversation_list__list_item__timestamp: {
    // fontFamily: "acumin-pro, san-serif",
    fontSize: 11,
    color: palette.grey,
    textAlign: "right",
  },
  no_conversations_component: {
    padding: 16,
  },
  no_conversations_component_text: {
    textAlign: "center",
    fontFamily: "acumin-pro, san-serif",
    color: palette.grey,
    fontWeight: "500",
  },
});
